/* eslint-disable @typescript-eslint/no-explicit-any */
import { serializeError } from 'serialize-error';
import { type CapErrorCategory, type CapFrustrationCategory, LOG_EVENT_TYPES } from 'src/constants';

import { useCommonLogger } from './use-common-logger';

const publishErrorMessage = (category: CapErrorCategory, logger?: any, error?: Error) => {
  const customError = new Error(error?.message);
  customError.name = 'CAP_ERROR_SIGNAL';
  customError.stack = error?.stack;
  customError.cause = error;

  if (typeof window !== 'undefined') {
    const ddRumObj = (window as any).DD_RUM;
    if (ddRumObj) {
      ddRumObj.addError(customError, {
        capErrorCategory: category,
      });
    }
  }

  if (logger) {
    logger.logEvent(LOG_EVENT_TYPES.CAP_ERROR, {
      error: serializeError(customError),
    });
  }
};

const publishFrustrationMessage = (category: CapFrustrationCategory, logger?: any, error?: Error) => {
  const customError = new Error(error?.message);
  customError.name = 'CAP_FRUSTRATION_SIGNAL';
  customError.stack = error?.stack;
  customError.cause = error;

  if (typeof window !== 'undefined') {
    const ddRumObj = (window as any).DD_RUM;
    if (ddRumObj) {
      ddRumObj.addError(customError, {
        capErrorCategory: category,
      });
    }
  }

  if (logger) {
    logger.logEvent(LOG_EVENT_TYPES.CAP_FRUSTRATION, {
      frustration: serializeError(customError),
    });
  }
};

const publishActionMessage = (actionName: string, context: any, logger?: any) => {
  if (typeof window !== 'undefined') {
    const ddRumObj = (window as any).DD_RUM;
    if (ddRumObj) {
      ddRumObj.addAction(actionName, {
        ...context,
      });
    }
  }

  if (logger) {
    logger.logEvent(LOG_EVENT_TYPES.CAP_ACTION, {
      action: actionName,
      context,
    });
  }
};

/**
 * Custom hook that bundles the error, frustration, and action publishing logic.
 */
const useCapSignals = () => {
  const logger = useCommonLogger();

  // will log and do custom datadog RUM event
  const publishError = (category: CapErrorCategory, error?: Error) => {
    publishErrorMessage(category, logger, error);
  };

  // will log and do custom datadog RUM event
  const publishFrustration = (category: CapFrustrationCategory, error?: Error) => {
    publishFrustrationMessage(category, logger, error);
  };

  const publishAction = (action: string, context: any) => {
    publishActionMessage(action, context, logger);
  };

  return {
    publishError,
    publishFrustration,
    publishAction,
  };
};

export { useCapSignals, publishErrorMessage, publishFrustrationMessage, publishActionMessage };
